import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { ChevronRight, Grid2x2, LayoutGrid, List, SlidersHorizontal, X } from 'lucide-react';
import api from '../services/api';
import ProductCard from '../components/product/ProductCard';

const SORT_OPTIONS = [
  { value: 'newest', label: 'Date, new to old' },
  { value: 'price_asc', label: 'Price, low to high' },
  { value: 'price_desc', label: 'Price, high to low' },
  { value: 'sort_order', label: 'Featured' },
];

const PER_PAGE_OPTIONS = [12, 24, 48];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<any[]>([]);
  const [category, setCategory] = useState<any>(null);
  const [pagination, setPagination] = useState({ total: 0, current_page: 1, last_page: 1 });
  const [loading, setLoading] = useState(true);
  const [gridCols, setGridCols] = useState(3);
  const [sortBy, setSortBy] = useState('newest');
  const [perPage, setPerPage] = useState(15);
  const [filterOpen, setFilterOpen] = useState(false);
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });

  const fetchProducts = useCallback((page = 1) => {
    setLoading(true);
    const params: any = { page, per_page: perPage, sort_by: sortBy };
    if (slug) params.category = slug;
    if (priceRange.min) params.min_price = priceRange.min;
    if (priceRange.max) params.max_price = priceRange.max;
    if (searchParams.get('search')) params.search = searchParams.get('search');

    api.get('/products', { params })
      .then(r => {
        setProducts(r.data.data || r.data);
        if (r.data.meta) setPagination({ total: r.data.meta.total, current_page: r.data.meta.current_page, last_page: r.data.meta.last_page });
        else setPagination({ total: r.data.total || 0, current_page: r.data.current_page || 1, last_page: r.data.last_page || 1 });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug, sortBy, perPage, priceRange, searchParams]);

  useEffect(() => {
    if (slug) {
      api.get(`/categories/${slug}`).then(r => setCategory(r.data)).catch(() => {});
    } else {
      setCategory(null);
    }
    fetchProducts(1);
  }, [slug, sortBy, perPage]);

  const gridConfig = {
    2: 'grid-cols-2',
    3: 'grid-cols-2 md:grid-cols-3',
    4: 'grid-cols-2 md:grid-cols-4',
  };

  const title = slug === 'special-prices' ? 'Special Prices' :
    searchParams.get('search') ? `Search: "${searchParams.get('search')}"` :
    category?.name || 'All Products';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-xs text-gray-500 mb-6">
        <Link to="/" className="hover:text-gray-900">Home</Link>
        <ChevronRight size={14} />
        <span className="text-gray-900 capitalize">{title}</span>
      </nav>

      <div className="flex gap-6">
        {/* Sidebar - Desktop */}
        <aside className="hidden lg:block w-56 flex-shrink-0">
          <div className="sticky top-24 space-y-8">
            <div>
              <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-widest mb-4">Categories</h3>
              {[
                { name: 'SALE', slug: 'special-prices' },
                { name: 'NEW IN', slug: 'new-in' },
                { name: 'MEN', slug: 'men' },
                { name: 'WOMEN', slug: 'women' },
                { name: 'JUNIOR', slug: 'junior' },
                { name: 'ETHNIC WEAR', slug: 'ethnic' },
                { name: 'INNERWEAR', slug: 'innerwear' },
                { name: 'SPORTSWEAR', slug: 'sportswear' },
                { name: 'WINTER', slug: 'winter' },
                { name: 'SUMMER', slug: 'summer' },
              ].map(cat => (
                <Link
                  key={cat.slug}
                  to={cat.slug === 'special-prices' || cat.slug === 'new-in' ? `/${cat.slug}` : `/category/${cat.slug}`}
                  className={`block py-2 text-sm border-b border-gray-50 transition-colors ${slug === cat.slug ? 'text-gray-900 font-semibold' : 'text-gray-500 hover:text-gray-900'}`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div>
              <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-widest mb-4">Price Range</h3>
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={priceRange.min}
                  onChange={e => setPriceRange(p => ({ ...p, min: e.target.value }))}
                  className="w-20 border border-gray-300 px-2 py-1.5 text-xs focus:outline-none focus:border-gray-900"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={priceRange.max}
                  onChange={e => setPriceRange(p => ({ ...p, max: e.target.value }))}
                  className="w-20 border border-gray-300 px-2 py-1.5 text-xs focus:outline-none focus:border-gray-900"
                />
              </div>
              <button onClick={() => fetchProducts(1)} className="mt-2 w-full bg-gray-900 text-white py-1.5 text-xs font-medium">Apply</button>
            </div>
          </div>
        </aside>

        {/* Products Grid */}
        <div className="flex-1 min-w-0">
          {/* Toolbar */}
          <div className="mb-5 pb-4 border-b border-gray-100">
            {/* Mobile row */}
            <div className="flex items-center justify-between gap-2 lg:hidden">
              <button
                onClick={() => setFilterOpen(true)}
                className="flex items-center gap-2 border border-gray-300 px-3.5 py-2 text-sm font-medium text-gray-700 hover:border-gray-500 transition-colors rounded-sm"
              >
                <SlidersHorizontal size={14} /> Filters
              </button>
              <div className="flex items-center gap-1.5">
                <div className="flex gap-1">
                  {[2, 3].map(cols => (
                    <button key={cols} onClick={() => setGridCols(cols as 2|3|4)} className={`p-1.5 border transition-colors rounded-sm ${gridCols === cols ? 'border-gray-900 text-gray-900 bg-gray-50' : 'border-gray-200 text-gray-400 hover:border-gray-400'}`}>
                      {cols === 2 ? <List size={13} /> : <Grid2x2 size={13} />}
                    </button>
                  ))}
                </div>
                <select aria-label="Sort by" value={sortBy} onChange={e => setSortBy(e.target.value)} className="border border-gray-300 px-2 py-2 text-sm focus:outline-none rounded-sm text-gray-700 bg-white max-w-[150px]">
                  {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
            {/* Desktop row */}
            <div className="hidden lg:flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">View as</span>
                <div className="flex gap-1">
                  {[2, 3, 4].map(cols => (
                    <button key={cols} onClick={() => setGridCols(cols as 2|3|4)} className={`p-1.5 border transition-colors ${gridCols === cols ? 'border-gray-900 text-gray-900' : 'border-gray-200 text-gray-400 hover:border-gray-400'}`}>
                      {cols === 2 ? <List size={14} /> : cols === 3 ? <Grid2x2 size={14} /> : <LayoutGrid size={14} />}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span>Items per page:</span>
                  <select aria-label="Items per page" value={perPage} onChange={e => setPerPage(Number(e.target.value))} className="border border-gray-300 px-2 py-1 text-sm focus:outline-none">
                    {PER_PAGE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span>Sort by:</span>
                  <select aria-label="Sort by" value={sortBy} onChange={e => setSortBy(e.target.value)} className="border border-gray-300 px-2 py-1 text-sm focus:outline-none">
                    {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Filter Drawer */}
          {filterOpen && (
            <div className="fixed inset-0 z-50 lg:hidden">
              <div className="absolute inset-0 bg-black/40" onClick={() => setFilterOpen(false)} />
              <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl max-h-[80vh] flex flex-col">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                  <h2 className="text-base font-semibold text-gray-900">Filters</h2>
                  <button aria-label="Close filters" onClick={() => setFilterOpen(false)} className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
                    <X size={18} />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-7">
                  {/* Categories */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-widest mb-3">Categories</h3>
                    <div className="grid grid-cols-2 gap-1.5">
                      {[
                        { name: 'SALE', slug: 'special-prices' },
                        { name: 'NEW IN', slug: 'new-in' },
                        { name: 'MEN', slug: 'men' },
                        { name: 'WOMEN', slug: 'women' },
                        { name: 'JUNIOR', slug: 'junior' },
                        { name: 'ETHNIC WEAR', slug: 'ethnic' },
                        { name: 'INNERWEAR', slug: 'innerwear' },
                        { name: 'SPORTSWEAR', slug: 'sportswear' },
                        { name: 'WINTER', slug: 'winter' },
                        { name: 'SUMMER', slug: 'summer' },
                      ].map(cat => (
                        <Link
                          key={cat.slug}
                          to={cat.slug === 'special-prices' || cat.slug === 'new-in' ? `/${cat.slug}` : `/category/${cat.slug}`}
                          onClick={() => setFilterOpen(false)}
                          className={`px-3 py-2.5 text-xs font-medium border rounded-lg text-center transition-colors ${slug === cat.slug ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 text-gray-600 hover:border-gray-400 hover:text-gray-900'}`}
                        >
                          {cat.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                  {/* Price */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-widest mb-3">Price Range</h3>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        placeholder="Min"
                        value={priceRange.min}
                        onChange={e => setPriceRange(p => ({ ...p, min: e.target.value }))}
                        className="flex-1 border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:border-gray-900 rounded-lg"
                      />
                      <span className="text-gray-400 text-sm flex-shrink-0">—</span>
                      <input
                        type="number"
                        placeholder="Max"
                        value={priceRange.max}
                        onChange={e => setPriceRange(p => ({ ...p, max: e.target.value }))}
                        className="flex-1 border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:border-gray-900 rounded-lg"
                      />
                    </div>
                  </div>
                  {/* Items per page */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-widest mb-3">Items Per Page</h3>
                    <div className="flex gap-2">
                      {PER_PAGE_OPTIONS.map(n => (
                        <button
                          key={n}
                          onClick={() => setPerPage(n)}
                          className={`px-4 py-2 text-sm border rounded-lg transition-colors ${perPage === n ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="px-5 py-4 border-t border-gray-100 flex gap-3">
                  <button
                    onClick={() => { setPriceRange({ min: '', max: '' }); }}
                    className="flex-1 border border-gray-300 text-gray-700 py-3 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Clear All
                  </button>
                  <button
                    onClick={() => { fetchProducts(1); setFilterOpen(false); }}
                    className="flex-1 bg-gray-900 text-white py-3 text-sm font-medium rounded-xl hover:bg-gray-800 transition-colors"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className={`grid ${gridConfig[gridCols as keyof typeof gridConfig]} gap-4`}>
              {Array(12).fill(null).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-100 aspect-[3/4]" />
                  <div className="h-4 bg-gray-100 mt-3 w-3/4" />
                  <div className="h-4 bg-gray-100 mt-2 w-1/2" />
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">No products found</p>
              <Link to="/" className="mt-4 inline-block text-gray-600 underline">Go back home</Link>
            </div>
          ) : (
            <>
              <div className={`grid ${gridConfig[gridCols as keyof typeof gridConfig]} gap-4 md:gap-6`}>
                {products.map(product => <ProductCard key={product.id} product={product} />)}
              </div>

              {/* Pagination */}
              {pagination.last_page > 1 && (
                <div className="flex justify-center gap-2 mt-12">
                  {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => fetchProducts(page)}
                      className={`w-9 h-9 text-sm font-medium border transition-colors ${page === pagination.current_page ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 text-gray-700 hover:border-gray-900'}`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
